/**
 * components/landing/platform/sections/WhyChoose.jsx — §3.10.
 *
 * Outcome-focused reasons.  8 tiles, each with a headline + short body.
 * Deliberately outcome-first ("faster deployment") rather than feature-
 * first ("has Kubernetes").
 */

import React from 'react'
import clsx from 'clsx'
import {
  Layers, Rocket, MinusCircle, Sparkles, Bot, Building2, TrendingUp, RefreshCw,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { WHY } from '../data'

const ICONS = { Layers, Rocket, MinusCircle, Sparkles, Bot, Building2, TrendingUp, RefreshCw }
const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function WhyChoose() {
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Why businesses choose Kalki Ai Cloud</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Outcomes, <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">not features.</span></>}
            subtitle="The reason businesses choose Kalki Ai Cloud isn’t any single feature — it’s what those features let them achieve together, and how quickly."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {WHY.map((w) => {
            const tone = TONE[w.tone] || TONE.orange
            const Icon = ICONS[w.iconKey] || Sparkles
            return (
              <div key={w.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px]">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex flex-col gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div>
                    <h4 className="text-[15px] font-semibold text-white leading-tight">{w.label}</h4>
                    <p className="text-[13px] text-zinc-400 leading-relaxed mt-1">{w.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
